import React from 'react';
import type { EvaluationItem, CodeSubmissionConfig } from '../../../../../types/course';
import FloatingLabelPill from '../shared/FloatingLabelPill';
import SectionHeader from '../shared/SectionHeader';
import TrashButton from '../shared/TrashButton';

interface ItemEditorProps {
  item: Extract<EvaluationItem, { type: 'code_submission' }>;
  onChange: (patch: Partial<Extract<EvaluationItem, { type: 'code_submission' }>>) => void;
  onDelete: () => void;
  readOnly?: boolean;
}

const LANGUAGES = ['python', 'javascript', 'typescript', 'java', 'go', 'rust', 'cpp', 'sql'] as const;

const codeTextareaClass =
  'w-full min-h-[140px] px-[18px] py-3.5 rounded-2xl border border-text-secondary bg-transparent text-text-primary font-mono text-[13px] leading-5 outline-none focus:border-primary-500 transition-colors resize-y';

const selectClass =
  'h-[46px] px-[18px] rounded-[50px] border border-text-secondary bg-transparent text-text-primary font-sans text-sm outline-none focus:border-primary-500 transition-colors';

export default function CodeSubmissionItem({ item, onChange, onDelete, readOnly }: ItemEditorProps) {
  const { prompt, language, starter_code, expected_output } = item.config;

  const updateConfig = (patch: Partial<CodeSubmissionConfig>) => {
    onChange({ config: { ...item.config, ...patch } });
  };

  return (
    <div className="flex flex-col gap-[31px] w-full">
      <SectionHeader title="Code Submission" />
      <FloatingLabelPill
        label="Prompt"
        value={prompt}
        onChange={(v) => updateConfig({ prompt: v })}
        placeholder="Describe the coding task"
        readOnly={readOnly}
        multiline
        rows={3}
        trailingIcon={!readOnly && <TrashButton onClick={onDelete} ariaLabel="Delete question" />}
      />
      <label className="flex flex-col gap-2 font-sans text-xs text-text-secondary">
        Language
        <select
          value={language}
          onChange={(e) => updateConfig({ language: e.target.value })}
          disabled={readOnly}
          className={selectClass}
        >
          {LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-2 font-sans text-xs text-text-secondary">
        Starter code (optional)
        <textarea
          value={starter_code ?? ''}
          onChange={(e) => updateConfig({ starter_code: e.target.value })}
          placeholder={`# starter ${language} code here`}
          disabled={readOnly}
          className={codeTextareaClass}
        />
      </label>
      <label className="flex flex-col gap-2 font-sans text-xs text-text-secondary">
        Expected output (optional)
        <textarea
          value={expected_output ?? ''}
          onChange={(e) => updateConfig({ expected_output: e.target.value })}
          placeholder="The program should print…"
          disabled={readOnly}
          className={codeTextareaClass}
        />
      </label>
    </div>
  );
}
